import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { fileUrl } from "@/lib/api";
import { formatGNF } from "@/lib/format";
import { CaretLeft, CaretRight, Star, MapPin, Bed, Ruler } from "@phosphor-icons/react";

const ROTATE_MS = 5000;

export default function FeaturedCarousel({ items = [] }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  const total = items.length;

  useEffect(() => {
    if (total <= 1 || paused) return;
    timerRef.current = setInterval(() => {
      setIndex((i) => (i + 1) % total);
    }, ROTATE_MS);
    return () => clearInterval(timerRef.current);
  }, [total, paused]);

  if (!total) return null;

  const go = (delta) => {
    setIndex((i) => (i + delta + total) % total);
  };

  return (
    <section
      className="mx-auto max-w-7xl px-4 pt-4 md:px-8 md:pt-8"
      data-testid="featured-carousel"
    >
      <div className="mb-6 flex items-end justify-between">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.25em] text-primary">
            <Star size={12} weight="fill" /> À la une
          </span>
          <h2 className="mt-2 font-display text-3xl font-black md:text-4xl">
            Annonces boostées
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => go(-1)}
            aria-label="Précédent"
            data-testid="carousel-prev"
            className="btn-pill flex h-10 w-10 items-center justify-center rounded-full border-2 border-border bg-card hover:border-primary hover:bg-primary hover:text-primary-foreground"
          >
            <CaretLeft size={18} weight="bold" />
          </button>
          <button
            onClick={() => go(1)}
            aria-label="Suivant"
            data-testid="carousel-next"
            className="btn-pill flex h-10 w-10 items-center justify-center rounded-full border-2 border-border bg-card hover:border-primary hover:bg-primary hover:text-primary-foreground"
          >
            <CaretRight size={18} weight="bold" />
          </button>
        </div>
      </div>

      <div
        className="relative overflow-hidden rounded-3xl border border-border bg-card"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        data-testid="carousel-track"
      >
        <div
          className="flex transition-transform duration-700 ease-out"
          style={{ transform: `translateX(-${index * 100}%)` }}
        >
          {items.map((l) => (
            <CarouselSlide key={l.listing_id} listing={l} />
          ))}
        </div>

        {/* Dots */}
        <div className="pointer-events-none absolute inset-x-0 bottom-4 flex justify-center gap-1.5">
          {items.map((_, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              aria-label={`Aller à la diapositive ${i + 1}`}
              data-testid={`carousel-dot-${i}`}
              className={`pointer-events-auto h-2 rounded-full transition-all ${
                i === index ? "w-6 bg-primary" : "w-2 bg-white/70"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function CarouselSlide({ listing }) {
  const photo = listing.photos?.[0];
  return (
    <Link
      to={`/annonce/${listing.listing_id}`}
      data-testid={`carousel-slide-${listing.listing_id}`}
      className="relative flex min-w-full flex-col md:h-[420px] md:flex-row"
    >
      <div className="relative aspect-[16/9] w-full overflow-hidden bg-muted md:aspect-auto md:h-full md:w-3/5">
        {photo ? (
          <img
            src={fileUrl(photo, 1000)}
            alt={listing.title}
            className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className="flex h-full items-center justify-center text-muted-foreground">
            Aucune photo
          </div>
        )}
        <span className="absolute left-4 top-4 flex items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-xs font-bold uppercase tracking-widest text-primary-foreground">
          <Star size={14} weight="fill" /> À la une
        </span>
      </div>

      <div className="flex flex-1 flex-col justify-center gap-3 p-6 md:p-10">
        <span className="text-xs font-bold uppercase tracking-[0.25em] text-primary">
          {listing.category}{" "}
          {listing.transaction_type === "location" ? "· À louer" : "· À vendre"}
        </span>
        <h3 className="font-display text-2xl font-black leading-tight md:text-3xl">
          {listing.title}
        </h3>
        <div className="flex items-center gap-1 text-sm text-muted-foreground">
          <MapPin size={16} weight="duotone" /> {listing.quartier}, {listing.city}
        </div>
        <div className="mt-2 font-display text-3xl font-black text-primary">
          {formatGNF(listing.price)}
          {listing.transaction_type === "location" && (
            <span className="text-sm font-normal text-muted-foreground"> / mois</span>
          )}
        </div>
        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
          {listing.chambres != null && (
            <span className="inline-flex items-center gap-1">
              <Bed size={16} weight="duotone" /> {listing.chambres} ch.
            </span>
          )}
          {listing.superficie != null && (
            <span className="inline-flex items-center gap-1">
              <Ruler size={16} weight="duotone" /> {listing.superficie} m²
            </span>
          )}
        </div>
        <span className="btn-pill mt-3 inline-flex w-fit items-center gap-1 rounded-full bg-secondary px-5 py-2.5 text-sm font-bold text-secondary-foreground hover:bg-secondary/90">
          Voir l'annonce →
        </span>
      </div>
    </Link>
  );
}
